import {Component, EventEmitter, Input, Output} from '@angular/core';
import {bsToGregorian, gregorianToBs, NepaliDatepickerComponent} from "./nepali-datepicker/nepali-datepicker.component";

import {FormsModule} from '@angular/forms';
import {ConnectionPositionPair, OverlayModule} from '@angular/cdk/overlay';

export type DateRestriction = 'none' | 'past-only' | 'future-only';

@Component({
  selector: 'app-nepali-date-picker-wrapper',
  templateUrl: './nepali-date-picker-wrapper.component.html',
  imports: [NepaliDatepickerComponent, FormsModule, OverlayModule],
  standalone: true,
  styleUrl: './nepali-date-picker-wrapper.component.scss'
})
export class NepaliDatePickerWrapperComponent {
  private _date: string | null = null;

  overlayPositions: ConnectionPositionPair[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 6
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 6
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -6
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -6
    },
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 6
    },
    {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -6
    }
  ];

  @Input() disabled: boolean = false;
  @Input() dateIn: 'BS' | 'AD' = 'AD';
  @Input() position: string = '';
  @Input() dateRestriction: DateRestriction = 'none';
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() enableTime: boolean = false;
  timeSelectionMode = false;
  selectedTime = '00:00';
  tempSelectedDateString: string | null = null;

  @Input()
  set date(value: string | null) {
    this._date = value;
    if (this._date) {
      let datePart = this._date;
      if (this._date.includes('T')) {
        datePart = this._date.split('T')[0];
        this.selectedTime = this._date.split('T')[1].substring(0, 5);
      } else if (this._date.includes(' ')) {
        datePart = this._date.split(' ')[0];
        this.selectedTime = this._date.split(' ')[1].substring(0, 5);
      } else {
        this.selectedTime = '00:00';
      }
      const [year, month, day] = datePart.split('-').map(Number);
      if (this.dateIn === 'BS') {
        this.selectedDate = {y: year, m: month - 1, d: day};
      } else {
        this.selectedDate = gregorianToBs(new Date(year, month - 1, day));
      }
    } else {
      this.selectedDate = null;
    }
  }

  @Input() placeholder: string = 'Select Date';
  @Output() dateSelected = new EventEmitter<string>();
  showCalendar = false;
  selectedDate: { y: number; m: number; d: number } | null = null;

  get displayValue(): string {
    if (!this.selectedDate) return '';
    const year = this.selectedDate.y;
    const month = (this.selectedDate.m + 1).toString().padStart(2, '0');
    const day = this.selectedDate.d.toString().padStart(2, '0');
    let str = `${year}/${month}/${day}`;
    if (this.enableTime) {
      str += ` ${this.selectedTime}`;
    }
    return str;
  }

  // NEW: Convert min/max dates to BS format
  get effectiveMinDateBS(): { y: number; m: number; d: number } | undefined {
    let minDateToUse = this.minDate;

    if (!minDateToUse && this.dateRestriction === 'future-only') {
      // Future only: minimum is today
      minDateToUse = new Date();
      minDateToUse.setHours(0, 0, 0, 0);
    }

    if (minDateToUse) {
      return gregorianToBs(minDateToUse);
    }
    return undefined;
  }

  get effectiveMaxDateBS(): { y: number; m: number; d: number } | undefined {
    let maxDateToUse = this.maxDate;

    if (!maxDateToUse && this.dateRestriction === 'past-only') {
      // Past only: maximum is today
      maxDateToUse = new Date();
      maxDateToUse.setHours(23, 59, 59, 999);
    }

    if (maxDateToUse) {
      return gregorianToBs(maxDateToUse);
    }
    return undefined;
  }

  onDateSelected(date: { y: number; m: number; d: number }) {
    this.selectedDate = date;

    let baseDateStr = '';
    if (this.dateIn === 'BS') {
      baseDateStr = `${date.y}-${(date.m + 1).toString().padStart(2, '0')}-${date.d.toString().padStart(2, '0')}`;
    } else {
      const gregorianDate = bsToGregorian(date);
      baseDateStr = gregorianDate.toISOString().split('T')[0];
    }

    if (this.enableTime) {
      this.tempSelectedDateString = baseDateStr;
      this.timeSelectionMode = true;
    } else {
      this.showCalendar = false;
      this.dateSelected.emit(baseDateStr);
    }
  }

  confirmTimeSelection() {
    if (this.tempSelectedDateString) {
      this.dateSelected.emit(this.tempSelectedDateString + 'T' + this.selectedTime + ':00');
    }
    this.timeSelectionMode = false;
    this.showCalendar = false;
  }

  openCalendar(event: MouseEvent) {
    if (this.disabled) return;
    this.timeSelectionMode = false;
    this.tempSelectedDateString = null;
    event.stopPropagation();
    this.showCalendar = true;
  }
}
